import { useSchoolSchoolyear } from 'containers/App/context/SchoolyearSchoolProvider';
import { CJEvent } from 'types/event';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import request from 'utils/request';
import { format } from 'utils/time/format';
import { EVENTS_KEY } from 'containers/CahierJournal/queries/useCJSeances';
import groupBy from 'utils/arrays/groupBy';
import { addToCJPastAtom } from 'containers/CahierJournal/queries/historyAtoms';
import {
  deserializeEvent,
  serializeCJSeanceUpdateView,
  cleanPendingEphemeralGroups,
} from 'containers/CahierJournal/queries/serializer';
import { useUser } from '@teetsh/app/src/containers/App/context/useUser';
import useNotification from 'containers/App/Notifications/useNotification';
import { useAtom } from 'jotai';
import { CAHIER_JOURNAL_DAYS_WITH_EVENTS_KEY } from 'containers/CahierJournal/queries/useGetDaysWithEvents';
import { useTranslation } from 'react-i18next';
import { EPHEMERAL_GROUPS_KEY } from 'queries/eleveGroups/useEphemeralEleveGroups';

interface Props {
  cjEvents: CJEvent[];
  schoolyearId?: string;
  schoolId?: string;
}

interface ContextItem {
  day: string;
  previousEvents: CJEvent[];
}

function createCJ({
  schoolyearId,
  schoolId,
  cjEvents,
  location,
}: {
  schoolyearId: string;
  schoolId: string;
  cjEvents: any;
  location: string;
}) {
  const requestURL = `/schoolyears/${schoolyearId}/schools/${schoolId}/cahier-journal/seances`;
  return request(requestURL, {
    method: 'POST',
    body: {
      seances: cjEvents
        .filter(e => e.id !== 'add-event')
        .map(event => serializeCJSeanceUpdateView({ ...event }, location)),
    },
  }).then(({ data }) => (data || []).map(ev => deserializeEvent(ev, location)));
}

export function useCreateCJSeances(disableUndoRedo?: boolean) {
  const { schoolyear, school } = useSchoolSchoolyear();
  const { notifyError } = useNotification();
  const user = useUser();
  const { t } = useTranslation(['cahierJournal']);
  const queryClient = useQueryClient();
  const [, addToPast] = useAtom(addToCJPastAtom);
  const { mutateAsync } = useMutation<CJEvent[], any, Props, ContextItem[]>({
    mutationFn: ({ cjEvents, schoolyearId, schoolId }) =>
      createCJ({
        schoolyearId: schoolyearId || schoolyear.id,
        schoolId: schoolId || school.id,
        cjEvents,
        location: user.location,
      }),
    retry: 1,
    onSuccess: (returnedEvents, { cjEvents }) => {
      // Clean pendingEphemeralGroups before putting in cache
      const cleanedEvents = returnedEvents.map(cleanPendingEphemeralGroups);
      Object.entries(groupBy(cleanedEvents, e => format(e.start, 'fr-FR'))).map(
        ([d, newEvents]) => {
          const newEventsIds = newEvents.map(e => e.id);
          const previousEvents = queryClient.getQueryData<CJEvent[]>([
            schoolyear.id,
            school.id,
            EVENTS_KEY,
            d,
          ]);
          queryClient.setQueryData<CJEvent[]>(
            [schoolyear.id, school.id, EVENTS_KEY, d],
            old => [
              // le filter c'est pour gérer le cas d'un séance qui fail à être ajouté
              // mais ça fonctionne au retry
              ...(old || []).filter(e => !newEventsIds.includes(e.id)),
              ...newEvents,
            ],
          );
          return { day: d, previousEvents: previousEvents || [] };
        },
      );
      // trackEvent('CJ seance created');
      if (!disableUndoRedo) {
        addToPast({
          payload: cjEvents,
          action: 'CREATE',
        });
      }

      queryClient.invalidateQueries({
        queryKey: [
          schoolyear.id,
          school.id,
          CAHIER_JOURNAL_DAYS_WITH_EVENTS_KEY,
        ],
      });

      queryClient.invalidateQueries({
        queryKey: [schoolyear.id, school.id, EPHEMERAL_GROUPS_KEY],
      });
    },
    onError: (err, { cjEvents }) => {
      notifyError({
        title: t('cahierJournal:messages.createError', {
          count: (cjEvents || []).length,
        }),
      });
      throw err;
    },
  });
  return mutateAsync;
}
